/**
 * Password strength policy — the client-side mirror.
 *
 * This drives the live strength meter and stops a weak password before it ever
 * leaves the browser. It is a convenience, not the gate: the same rules are
 * enforced in backend/password_policy.py, which is what actually decides
 * whether a password is stored.
 *
 * Keep in sync with backend/password_policy.py — same constants, same
 * blacklist, same normalisation. If you change a rule here, change it there too.
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const PASSWORD_REQUIRED_CLASSES = 3;

// The four character classes a password is scored against. "Special" is
// anything that is not an ASCII letter or digit, so unicode and punctuation
// both count.
const CLASS_PATTERNS = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/];

// Digits/symbols that are routinely substituted for letters. Undoing them
// before the blacklist lookup is what makes "P@ssw0rd" fail alongside
// "password" — without it, leet spellings sail past the length and
// character-class rules.
const LEET_MAP = [
  ["@", "a"], ["4", "a"], ["8", "b"], ["(", "c"], ["3", "e"], ["6", "g"],
  ["1", "i"], ["!", "i"], ["|", "i"], ["0", "o"], ["5", "s"], ["$", "s"],
  ["7", "t"], ["+", "t"], ["2", "z"],
];

export const COMMON_PASSWORDS = new Set([
  "password", "passwort", "passw", "pass", "parola", "sifre", "sifrem",
  "password1", "passworda", "passwordpassword", "letmein", "welcome",
  "welcome1", "admin", "administrator", "adminadmin", "root", "toor",
  "superadmin", "sysadmin", "operator", "analyst", "auditor", "guest",
  "user", "username", "test", "testtest", "testing", "demo", "sample",
  "default", "changeme", "change", "secret", "secrets", "temp", "temporary",
  "login", "logmein", "access", "master", "manager", "server", "system",
  "oracle", "mysql", "mongodb", "postgres", "database", "backup",
  "qwerty", "qwertyui", "qwertyuiop", "qwerty123", "asdf", "asdfgh",
  "asdfghjkl", "zxcvbn", "zxcvbnm", "qazwsx", "qwertz", "azerty",
  "abcabc", "abcd", "abcdef", "abcdefg", "abcdefgh", "abc", "aaaa",
  "iloveyou", "trustno", "trustnoone", "whatever", "sunshine", "princess",
  "football", "baseball", "basketball", "soccer", "hockey", "dragon",
  "monkey", "shadow", "jordan", "harley", "ranger", "hunter",
  "buster", "thomas", "tigger", "robert", "george", "charlie", "andrew",
  "michael", "michelle", "jessica", "jennifer", "daniel", "ashley",
  "matthew", "joshua", "freedom", "starwars", "superman", "batman",
  "pokemon", "computer", "internet", "samsung", "google", "facebook",
  "apple", "windows", "linux", "ubuntu", "android", "iphone",
  "summer", "winter", "spring", "autumn", "january", "february",
  "monday", "friday", "money", "love", "lovely", "flower", "chocolate",
  "cookie", "ginger", "pepper", "silver", "golden", "diamond", "purple",
  "orange", "yellow", "banana", "cheese", "coffee", "whisky", "vodka",
  "hello", "helloworld", "hallo", "merhaba", "selam", "ankara", "istanbul",
  "izmir", "galatasaray", "fenerbahce", "besiktas", "turkiye", "turkey",
  "cyberfortress", "dataflowx", "controlpanel", "firewall", "security",
  "secure", "private", "opensesame", "nopassword",
  "iiiiiiii", "oooooooo", "aaaaaaaa", "zzzzzzzz", "xxxxxxxx",
]);

// Straight runs across a keyboard row are as guessable as "1234".
const KEYBOARD_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm", "1234567890"];

export const STRENGTH_LABELS = ["Very weak", "Weak", "Fair", "Good", "Strong"];

const applyLeet = (text) =>
  LEET_MAP.reduce((acc, [symbol, letter]) => acc.split(symbol).join(letter), text);

const stripAffixes = (text) => text.replace(/^[^a-z]+/, "").replace(/[^a-z]+$/, "");

/**
 * The spellings of `password` the blacklist is checked against.
 *
 * "Summer2024!" and "P@ssw0rd" are the same weak password as "summer" and
 * "password" wearing a hat, so the affixes and the leet substitutions are
 * stripped before the lookup.
 */
const blacklistCandidates = (password) => {
  const base = password.toLowerCase();
  const leet = applyLeet(base);
  return [base, stripAffixes(base), leet, stripAffixes(leet)];
};

const countClasses = (password) =>
  CLASS_PATTERNS.filter((pattern) => pattern.test(password)).length;

/** Names a password must not be built out of: the username and the email. */
const identityTokens = ({ username, email } = {}) => {
  const tokens = [];
  if (username) tokens.push(String(username));
  if (email) {
    tokens.push(String(email));
    tokens.push(String(email).split("@")[0]);
  }
  // Anything shorter than three characters would match far too much.
  return tokens.filter((token) => token && token.length >= 3).map((t) => t.toLowerCase());
};

export const hasRepeatedRun = (password) => /(.)\1{2,}/.test(password);

/** True for runs like "abcd", "4321" or "asdf". */
export const hasSequentialRun = (password, length = 4) => {
  const lowered = password.toLowerCase();
  let ascending = 1;
  let descending = 1;
  for (let i = 1; i < lowered.length; i += 1) {
    const delta = lowered.charCodeAt(i) - lowered.charCodeAt(i - 1);
    ascending = delta === 1 ? ascending + 1 : 1;
    descending = delta === -1 ? descending + 1 : 1;
    if (ascending >= length || descending >= length) return true;
  }

  return KEYBOARD_ROWS.some((row) => {
    for (let i = 0; i + length <= row.length; i += 1) {
      const chunk = row.slice(i, i + length);
      const reversed = chunk.split("").reverse().join("");
      if (lowered.includes(chunk) || lowered.includes(reversed)) return true;
    }
    return false;
  });
};

/**
 * Score a password 0..4 for the meter. Advisory only — `checks` is what decides
 * whether the password is accepted. A password that breaks a hard rule never
 * scores above "Weak", so the bar can't say "Good" about something the form
 * will refuse.
 */
const scorePassword = (password, checks) => {
  let points = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) points += 1;
  if (password.length >= 12) points += 1;
  if (password.length >= 16) points += 1;
  points += countClasses(password) - 1;

  if (hasRepeatedRun(password)) points -= 1;
  if (hasSequentialRun(password)) points -= 1;

  points = Math.max(0, Math.min(6, points));
  const score = points <= 1 ? 0 : points === 2 ? 1 : points === 3 ? 2 : points <= 5 ? 3 : 4;

  const valid = Object.keys(checks).every((key) => checks[key]);
  return valid ? score : Math.min(score, 1);
};

/**
 * Check a candidate password against the policy.
 *
 * @param {string} password
 * @param {{username?: string, email?: string}} identity
 * @returns {{valid: boolean, errors: string[], score: number, label: string, checks: object}}
 *   `errors` is empty when the password is acceptable; `checks` carries one
 *   boolean per rule so the UI can render the requirement list.
 */
export function evaluatePassword(password, identity = {}) {
  const value = password == null ? "" : String(password);

  const checks = {
    length: value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH,
    classes: countClasses(value) >= PASSWORD_REQUIRED_CLASSES,
    noEdgeSpace: value === value.trim(),
    notCommon: !blacklistCandidates(value).some((candidate) =>
      COMMON_PASSWORDS.has(candidate),
    ),
    notPersonal: true,
  };

  const lowered = value.toLowerCase();
  const leetLowered = applyLeet(lowered);
  checks.notPersonal = !identityTokens(identity).some(
    (token) => lowered.includes(token) || leetLowered.includes(token),
  );

  const errors = [];
  if (value.length < PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`);
  } else if (value.length > PASSWORD_MAX_LENGTH) {
    errors.push(`Password must be at most ${PASSWORD_MAX_LENGTH} characters long.`);
  }
  if (!checks.classes) {
    errors.push(
      `Password must contain at least ${PASSWORD_REQUIRED_CLASSES} of the following: ` +
        "lowercase letter, uppercase letter, number, special character.",
    );
  }
  if (!checks.noEdgeSpace) errors.push("Password cannot start or end with a space.");
  if (!checks.notCommon) {
    errors.push("This password is too common. Please choose a less predictable one.");
  }
  if (!checks.notPersonal) {
    errors.push("Password cannot contain your username or email address.");
  }

  const score = scorePassword(value, checks);

  return {
    valid: errors.length === 0,
    errors,
    checks,
    score,
    label: STRENGTH_LABELS[score],
  };
}

/** The requirement list rendered under the strength bar, keyed by `checks`. */
export const PASSWORD_REQUIREMENTS = [
  { key: "length", text: `At least ${PASSWORD_MIN_LENGTH} characters` },
  {
    key: "classes",
    text: `At least ${PASSWORD_REQUIRED_CLASSES} of: lowercase, uppercase, number, special character`,
  },
  { key: "noEdgeSpace", text: "No leading or trailing spaces" },
  { key: "notCommon", text: "Not a commonly used password" },
  { key: "notPersonal", text: "Does not contain your username or email" },
];
